import { fetchConfig } from "@theme/utilities";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";

class AssortedPackComponent extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.requiredQuantity = Number(this.dataset.requiredQuantity || 3);
    this.assortedVariantId = Number(this.dataset.assortedVariantId);
    this.assortedVariantAvailable = this.dataset.assortedVariantAvailable === "true";
    this.assortedTitle = this.dataset.assortedTitle || "Assorted Pack";
    this.selection = this.#readSelection();
    this.form = this.closest("form[data-type='add-to-cart-form']");
    this.section = this.closest(".shopify-section");
    this.toggleButton = this.querySelector("[data-pack-select]");
    this.panel = this.querySelector(".assorted-pack__panel");
    this.totalLabel = this.querySelector(".assorted-pack__total");
    this.errorLabel = this.querySelector(".assorted-pack__error");
    this.hiddenPackType = this.querySelector('input[name="properties[_pack_type]"]');
    this.hiddenPackQuantity = this.querySelector('input[name="properties[_pack_quantity]"]');
    this.hiddenPackTitle = this.querySelector('input[name="properties[Pack]"]');
    this.hiddenFlavours = this.querySelector('input[name="properties[Flavours]"]');
    this.hiddenPackId = this.querySelector('input[name="properties[_assorted_pack_id]"]');
    this.rows = [...this.querySelectorAll(".assorted-pack__row")];
    this.normalPriceHTML = new Map();
    this.normalAddButtonDisabled = undefined;
    this.normalFeaturedMediaId = null;

    this.toggleButton?.addEventListener("click", () => this.#setAssortedSelected(this.dataset.selectedPack !== "assorted"));

    this.section?.addEventListener("click", this.#handleSectionClick);
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.#syncPickerState);

    this.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest("[data-flavour-action]");
      if (!button) return;

      const row = button.closest(".assorted-pack__row");
      if (!row) return;

      const flavourKey = row.dataset.flavourKey;
      const currentValue = Number(this.selection[flavourKey] || 0);
      const delta = button.dataset.flavourAction === "increase" ? 1 : -1;
      const nextValue = Math.max(0, currentValue + delta);
      if (delta > 0 && this.#getTotal() >= this.requiredQuantity) return;

      this.selection[flavourKey] = nextValue;
      this.dataset.selection = JSON.stringify(this.selection);
      this.#render();
    });

    this.form?.addEventListener("submit", this.#handleSubmit, true);
    this.#render();
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.#handleSubmit, true);
    this.section?.removeEventListener("click", this.#handleSectionClick);
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.#syncPickerState);
  }

  #setAssortedSelected(shouldSelect) {
    const isSelected = this.dataset.selectedPack === "assorted";
    if (shouldSelect === isSelected) return;

    if (shouldSelect) {
      this.normalAddButtonDisabled = this.form?.querySelector('button[type="submit"][name="add"]')?.disabled;
      this.normalFeaturedMediaId = this.#getActiveFeaturedMediaId();
    }
    this.dataset.selectedPack = shouldSelect ? "assorted" : "none";
    this.#render();
    this.#switchFeaturedMedia(shouldSelect ? this.dataset.assortedFeaturedMediaId : this.normalFeaturedMediaId);
    if (!shouldSelect) this.normalFeaturedMediaId = null;
  }

  #handleSectionClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const proxy = event.target.closest("[data-assorted-proxy]");
    if (proxy && this.section?.contains(proxy)) {
      if (!proxy.disabled) this.#setAssortedSelected(this.dataset.selectedPack !== "assorted");
      return;
    }

    if (this.dataset.selectedPack === "assorted" && event.target.closest(".variant-price-option")) {
      this.#setAssortedSelected(false);
    }
  };

  #syncPickerState = () => {
    const isSelected = this.dataset.selectedPack === "assorted";
    const picker = this.section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const proxies = this.section?.querySelectorAll("[data-assorted-proxy]") || [];

    if (picker) {
      if (proxies.length > 0 && isSelected) picker.setAttribute("data-assorted-selected", "true");
      else picker.removeAttribute("data-assorted-selected");
    }
    proxies.forEach((proxy) => proxy.setAttribute("aria-pressed", String(isSelected)));
  };

  #readSelection() {
    try {
      const parsed = JSON.parse(this.dataset.selection || "{}");
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    } catch (error) {
      console.warn("Assorted pack: invalid selection state", error);
    }

    return {};
  }

  #getTotal() {
    return Object.values(this.selection).reduce((sum, value) => sum + Number(value || 0), 0);
  }

  #getFlavourSummary() {
    return this.rows
      .map((row) => ({
        name: row.dataset.flavourName,
        quantity: Number(this.selection[row.dataset.flavourKey] || 0),
      }))
      .filter((flavour) => flavour.quantity > 0)
      .map((flavour) => `${flavour.name} x ${flavour.quantity}`)
      .join(", ");
  }

  #getActiveFeaturedMediaId() {
    const mediaGallery = this.section?.querySelector("media-gallery");
    const activeSlide =
      mediaGallery?.querySelector(".swiper-slide-active[data-media-id]") ||
      mediaGallery?.querySelector(".media-gallery__item.is-active[data-media-id]");
    return activeSlide?.dataset.mediaId || null;
  }

  #switchFeaturedMedia(mediaId) {
    if (!mediaId) return;

    const mediaGallery = this.section?.querySelector("media-gallery");
    if (!mediaGallery) return;

    mediaGallery.dispatchEvent(
      new CustomEvent("media:switch-optimistic", {
        detail: {
          variant: { featured_media: { id: mediaId } },
          forceMediaSwitch: true,
        },
      }),
    );
  }

  #render() {
    const isSelected = this.dataset.selectedPack === "assorted";
    const total = this.#getTotal();

    this.panel?.classList.toggle("hidden", !isSelected);
    this.toggleButton?.classList.toggle("is-selected", isSelected);
    this.toggleButton?.setAttribute("aria-pressed", String(isSelected));

    const variantPicker = this.section?.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );
    const hasPriceRows = Boolean(this.section?.querySelector("[data-assorted-proxy]"));
    if (variantPicker && !hasPriceRows) {
      variantPicker.hidden = isSelected;
      variantPicker.classList.toggle("hidden", isSelected);
    }
    this.#syncPickerState();

    const quantitySelector = this.form?.querySelector("quantity-selector-component");
    if (quantitySelector) {
      quantitySelector.hidden = isSelected;
      quantitySelector.classList.toggle("hidden", isSelected);
    }

    const acceleratedCheckout = this.form?.querySelector(".accelerated-checkout-block");
    if (acceleratedCheckout) {
      acceleratedCheckout.hidden = isSelected;
      acceleratedCheckout.classList.toggle("hidden", isSelected);
    }

    this.rows.forEach((row) => {
      const value = Number(this.selection[row.dataset.flavourKey] || 0);
      const countElement = row.querySelector("[data-count]");
      const decreaseButton = row.querySelector('[data-flavour-action="decrease"]');
      const increaseButton = row.querySelector('[data-flavour-action="increase"]');

      if (countElement) countElement.textContent = String(value);
      if (decreaseButton) decreaseButton.disabled = value <= 0;
      if (increaseButton) increaseButton.disabled = total >= this.requiredQuantity;
    });

    if (this.totalLabel) {
      this.totalLabel.textContent = `${total} / ${this.requiredQuantity} selected`;
    }

    const errorMessage = isSelected && total !== this.requiredQuantity
      ? `Select any ${this.requiredQuantity} packs to continue.`
      : isSelected && !this.assortedVariantAvailable
        ? "The Assorted Pack variant is unavailable."
        : "";
    if (this.errorLabel) this.errorLabel.textContent = errorMessage;

    if (this.hiddenPackType) {
      this.hiddenPackType.disabled = !isSelected;
      this.hiddenPackType.value = isSelected ? "assorted" : "";
    }

    if (this.hiddenPackQuantity) {
      this.hiddenPackQuantity.disabled = !isSelected;
      this.hiddenPackQuantity.value = isSelected ? String(this.requiredQuantity) : "";
    }

    if (this.hiddenPackTitle) {
      this.hiddenPackTitle.disabled = !isSelected;
      this.hiddenPackTitle.value = isSelected ? this.assortedTitle : "";
    }

    if (this.hiddenFlavours) {
      this.hiddenFlavours.disabled = !isSelected;
      this.hiddenFlavours.value = isSelected ? this.#getFlavourSummary() : "";
    }

    if (this.hiddenPackId) {
      this.hiddenPackId.disabled = !isSelected;
      if (isSelected && !this.hiddenPackId.value) {
        this.hiddenPackId.value = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      } else if (!isSelected) {
        this.hiddenPackId.value = "";
      }
    }

    const addButton = this.form?.querySelector('button[type="submit"][name="add"]');
    if (addButton && isSelected) {
      addButton.disabled =
        total !== this.requiredQuantity ||
        !this.assortedVariantAvailable ||
        !Number.isSafeInteger(this.assortedVariantId) ||
        this.assortedVariantId <= 0;
    } else if (addButton && this.normalAddButtonDisabled !== undefined) {
      addButton.disabled = this.normalAddButtonDisabled;
      this.normalAddButtonDisabled = undefined;
    }

    this.#renderPrices(isSelected);
  }

  #renderPrices(isSelected) {
    const containers = this.section?.querySelectorAll('product-price [ref="priceContainer"]') || [];
    if (isSelected) {
      containers.forEach((container) => {
        if (!this.normalPriceHTML.has(container)) this.normalPriceHTML.set(container, container.innerHTML);

        const regular = document.createElement("div");
        regular.className = "price__regular";
        const regularPrice = document.createElement("span");
        regularPrice.className = "price";
        regularPrice.textContent = this.dataset.assortedPrice || "";
        regular.append(regularPrice);

        const compareAtPrice = this.dataset.assortedCompareAtPrice || "";
        container.classList.toggle("price--on-sale", Boolean(compareAtPrice));
        container.classList.toggle("price--no-compare", !compareAtPrice);
        container.replaceChildren(regular);

        if (compareAtPrice) {
          const saleAmount = Number(this.dataset.assortedPriceAmount);
          const compareAtAmount = Number(this.dataset.assortedCompareAtPriceAmount);
          const discountPercent =
            Number.isSafeInteger(saleAmount) &&
            Number.isSafeInteger(compareAtAmount) &&
            compareAtAmount > saleAmount
              ? Math.round(((compareAtAmount - saleAmount) * 100) / compareAtAmount)
              : null;
          const sale = document.createElement("div");
          sale.className = "price__sale";
          const saleLabel = document.createElement("span");
          saleLabel.className = "visually-hidden";
          saleLabel.textContent = "Sale price";
          const salePrice = document.createElement("span");
          salePrice.className = "price price--sale";
          salePrice.textContent = this.dataset.assortedPrice || "";
          const regularLabel = document.createElement("span");
          regularLabel.className = "visually-hidden";
          regularLabel.textContent = "Regular price";
          const compare = document.createElement("span");
          compare.className = "compare-at-price";
          compare.textContent = compareAtPrice;
          sale.append(saleLabel, salePrice, regularLabel, compare);
          if (discountPercent !== null) {
            const discount = document.createElement("span");
            discount.className = "price-discount-tag";
            discount.textContent = `${discountPercent}% OFF`;
            sale.append(discount);
          }
          container.append(sale);
        }
      });
    } else if (this.normalPriceHTML.size > 0) {
      containers.forEach((container) => {
        const normalPriceHTML = this.normalPriceHTML.get(container);
        if (normalPriceHTML !== undefined) container.innerHTML = normalPriceHTML;
      });
      this.normalPriceHTML.clear();
    }
  }

  #handleSubmit = async (event) => {
    if (this.dataset.selectedPack !== "assorted") return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.#getTotal() !== this.requiredQuantity) {
      this.errorLabel.textContent = `Select any ${this.requiredQuantity} packs to continue.`;
      return;
    }

    if (!this.assortedVariantAvailable || !Number.isSafeInteger(this.assortedVariantId) || this.assortedVariantId <= 0) {
      this.errorLabel.textContent = "The Assorted Pack variant is unavailable.";
      return;
    }

    const submitButton = this.form?.querySelector('button[type="submit"][name="add"]');
    const addToCartComponent = submitButton?.closest("add-to-cart-component");
    addToCartComponent?.addLoading();
    if (submitButton) submitButton.disabled = true;

    try {
      const sectionIds = [...document.querySelectorAll("cart-items-component")]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      const requestBody = {
        items: [
          {
            id: this.assortedVariantId,
            quantity: 1,
            properties: {
              _pack_type: "assorted",
              _pack_quantity: String(this.requiredQuantity),
              Pack: this.assortedTitle,
              Flavours: this.#getFlavourSummary(),
              _assorted_pack_id: this.hiddenPackId?.value || "",
            },
          },
        ],
        sections: sectionIds.join(","),
        sections_url: window.location.pathname,
      };
      const fetchOptions = fetchConfig("json", { body: JSON.stringify(requestBody) });
      const addResponse = await fetch(window.FoxTheme.routes.cart_add_url, fetchOptions);
      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        throw new Error(addData.description || addData.message || "Unable to add the Assorted Pack to cart.");
      }

      const cartResponse = await fetch(window.FoxTheme.routes.cart);
      if (!cartResponse.ok) throw new Error("Unable to refresh the cart.");
      const cartData = await cartResponse.json();

      document.dispatchEvent(
        new CartUpdateEvent({}, "assorted-pack-component", {
          itemCount: Number(cartData.item_count || 0),
          source: "assorted-pack",
          productId: this.dataset.productId,
          sections: addData.sections || {},
        })
      );
    } catch (error) {
      console.error("Assorted pack add to cart failed:", error);
      this.errorLabel.textContent =
        error instanceof Error ? error.message : "Unable to add the Assorted Pack to cart. Please try again.";
    } finally {
      addToCartComponent?.removeLoading();
      this.#render();
    }
  };
}

if (!customElements.get("assorted-pack-component")) {
  customElements.define("assorted-pack-component", AssortedPackComponent);
}
